import { html, SP } from '../html.js';
import type { VNode } from '../html.js';
import type { Actions } from '../actions.js';
import type { ConsoleState } from '../store.js';
import { CopyButton } from '../ui/copy.js';

/**
 * The step between pasting a URL and the crawl starting.
 *
 * A SCREEN, not a modal over the landing page. The person pressed a button that
 * said Continue and something has to happen; a dialog floating over the form
 * they just filled in reads as an interruption of the crawl, while a screen
 * reads as the next step of it — which is what it is. It names the site in the
 * heading for the same reason: the one question worth answering here is "am I
 * still doing the thing I asked for", and the host is the answer.
 *
 * The email field is the same field the publish gate uses, deliberately. The
 * account a person opens here is the account that will own this crawl and the
 * one that will publish it later, so meeting it twice in two different shapes
 * would suggest there were two of them.
 *
 * There is no operator-secret path on this screen. The header exists for
 * scripts, an operator crawling from a browser is not a flow the runbook
 * describes, and putting a credential field in front of every visitor who
 * pasted a URL would be teaching the wrong reflex.
 */
export function SignInScreen(props: { state: ConsoleState; actions: Actions }): VNode {
  const { state, actions } = props;
  const sent = state.loginSent;
  const host = hostOf(state.pendingUrl);

  return html`
    <section class="wrap section signin">
      <h1 class="gate__h" tabindex="-1" data-focus-target>
        ${host ? `Sign in to crawl ${host}` : 'Sign in to start a crawl'}
      </h1>
      <p class="gate__p">
        A crawl belongs to an account from its first second — that is what puts it under
        <em>your</em> crawls rather than nobody's, and publishing what comes back needs the same
        account anyway. No password: a link to your inbox, and this browser stays signed in.
      </p>

      ${sent
        ? html`
            <div class="secret">
              <p class="gate__p" style="margin-bottom:10px">
                Check your inbox. If ${state.loginEmail.trim()} has an account, a sign-in link is on
                its way; it works once and expires. Opening it brings you back here with${SP}
                ${host ?? 'the address you typed'} still in the field, ready to crawl.
              </p>
              ${sent.devLink
                ? html`
                    <div class="disclaimer">
                      <div class="disclaimer__bar">
                        <span class="disclaimer__tag">Development mode · the link the mail would carry</span>
                        ${CopyButton({
                          id: 'signin-devlink',
                          text: sent.devLink,
                          copied: state.copied,
                          onCopy: actions.copy,
                          describe: 'the sign-in link',
                        })}
                      </div>
                      <p class="disclaimer__text">
                        This host's email provider is <code>log</code>, so nothing was mailed; the
                        link is printed here instead.${SP}
                        <a href=${sent.devLink}>Continue with this link</a>
                      </p>
                    </div>
                  `
                : null}
            </div>
          `
        : html`
            <form
              class="secret"
              onSubmit=${(e: Event) => {
                e.preventDefault();
                actions.login();
              }}
            >
              <label class="secret__label" for="login-email">Email</label>
              <input
                id="login-email"
                class="secret__input"
                type="email"
                autocomplete="email"
                spellcheck="false"
                placeholder="you@example.com"
                value=${state.loginEmail}
                aria-invalid=${state.loginError ? 'true' : 'false'}
                aria-describedby="signin-hint"
                onInput=${(e: Event) => actions.setLoginEmail((e.target as HTMLInputElement).value)}
              />
              <div class="gate__actions" style="margin-top:12px">
                <button type="submit" class="btn" disabled=${state.loginSending}>
                  ${state.loginSending ? 'Sending…' : 'Email me a sign-in link'}
                </button>
                <button type="button" class="linkish" onClick=${() => actions.cancelSignIn()}>
                  Not now
                </button>
              </div>
              ${state.loginError ? html`<p class="gate__err" role="alert">${state.loginError}</p>` : null}
              <p class="secret__hint" id="signin-hint">
                The address is used to sign you in and for nothing else. Nothing is crawled until you
                are back here and press the button yourself.
              </p>
            </form>
          `}
    </section>
  `;
}

/** The host alone: the heading is an answer to "which site", not a URL to read. */
function hostOf(url: string | null): string | null {
  if (!url) return null;
  try {
    return new URL(url).host;
  } catch {
    return null;
  }
}
